import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { TrendingUp, TrendingDown, Package, DollarSign, Download, FileText } from "lucide-react";
import { toast } from "sonner";

export default function AnalyticsPage() {
  const { t } = useI18n();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const downloadExport = async (type, format) => {
    try {
      const url = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("sp_token");
      const res = await fetch(`${url}/api/export/${type}/${format}`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `smartpriced_${type}.${format}`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`${format.toUpperCase()} ${t("toast.export_downloaded")}`);
    } catch { toast.error(t("toast.export_failed")); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t("common.loading")}</div>;

  let totalRevenue = 0, totalProfit = 0, avgMargin = 0, marginCount = 0;
  const marginBuckets = {};
  marginBuckets[t("analytics.negative")] = 0;
  marginBuckets["0-10%"] = 0;
  marginBuckets["10-20%"] = 0;
  marginBuckets["20-30%"] = 0;
  marginBuckets["30%+"] = 0;

  products.forEach((p) => {
    totalRevenue += p.price || 0;
    if (p.margin_pct != null) {
      marginCount++;
      avgMargin += p.margin_pct;
      if (p.margin_pct < 0) marginBuckets[t("analytics.negative")]++;
      else if (p.margin_pct < 10) marginBuckets["0-10%"]++;
      else if (p.margin_pct < 20) marginBuckets["10-20%"]++;
      else if (p.margin_pct < 30) marginBuckets["20-30%"]++;
      else marginBuckets["30%+"]++;
    }
    if (p.profit) totalProfit += p.profit;
  });
  if (marginCount > 0) avgMargin /= marginCount;

  const marginData = Object.entries(marginBuckets).map(([name, value]) => ({ name, value }));
  const COLORS = ["#EF4444", "#F59E0B", "#2563EB", "#10B981", "#8B5CF6"];

  const winning = products.filter((p) => p.cheapest_competitor && p.price <= p.cheapest_competitor.price).length;
  const losing = products.filter((p) => p.cheapest_competitor && p.price > p.cheapest_competitor.price).length;
  const posData = [{ name: t("analytics.cheapest"), value: winning }, { name: t("analytics.more_expensive"), value: losing }];

  const sortedByMargin = [...products].filter((p) => p.margin_pct != null).sort((a, b) => b.margin_pct - a.margin_pct);

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t("nav.analytics")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("analytics.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => downloadExport("products", "csv")} data-testid="export-csv-btn"><Download className="w-3.5 h-3.5 mr-1.5" />CSV</Button>
          <Button variant="outline" size="sm" onClick={() => downloadExport("products", "pdf")} data-testid="export-pdf-btn"><FileText className="w-3.5 h-3.5 mr-1.5" />PDF</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-card border-border"><CardContent className="p-5">
          <p className="text-sm text-muted-foreground">{t("analytics.products")}</p>
          <p className="text-2xl font-bold mt-1">{products.length}</p>
        </CardContent></Card>
        <Card className="bg-card border-border"><CardContent className="p-5">
          <p className="text-sm text-muted-foreground">{t("analytics.avg_margin")}</p>
          <p className={`text-2xl font-bold mt-1 font-mono-data ${avgMargin >= 0 ? "text-emerald-500" : "text-red-500"}`}>{avgMargin.toFixed(1)}%</p>
        </CardContent></Card>
        <Card className="bg-card border-border"><CardContent className="p-5">
          <p className="text-sm text-muted-foreground">{t("analytics.total_profit")}</p>
          <p className="text-2xl font-bold mt-1 font-mono-data text-emerald-500">{totalProfit.toFixed(0)} EUR</p>
        </CardContent></Card>
        <Card className="bg-card border-border"><CardContent className="p-5">
          <p className="text-sm text-muted-foreground">{t("analytics.cheapest_position")}</p>
          <p className="text-2xl font-bold mt-1">{winning} <span className="text-sm text-muted-foreground font-normal">{t("analytics.of")} {winning + losing}</span></p>
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-card border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">{t("analytics.margin_distribution")}</CardTitle></CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground mb-4">{t("analytics.margin_desc")}</p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={marginData}>
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px", color: "hsl(var(--foreground))" }} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>{marginData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">{t("analytics.competitive_position")}</CardTitle></CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground mb-4">{t("analytics.competitive_desc")}</p>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={posData} cx="50%" cy="50%" innerRadius={55} outerRadius={80} dataKey="value" paddingAngle={3}>
                  <Cell fill="#10B981" /><Cell fill="#EF4444" />
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px", color: "hsl(var(--foreground))" }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-6">
              <div className="flex items-center gap-2 text-xs"><div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />{t("analytics.cheapest")}: {winning}</div>
              <div className="flex items-center gap-2 text-xs"><div className="w-2.5 h-2.5 rounded-full bg-red-500" />{t("analytics.more_expensive")}: {losing}</div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-card border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-500" />{t("analytics.best_margin")}</CardTitle></CardHeader>
          <CardContent><div className="space-y-2">
            {sortedByMargin.slice(0, 5).map((p) => (
              <div key={p.id} className="flex items-center justify-between p-2 rounded-md hover:bg-accent/50">
                <span className="text-sm truncate flex-1">{p.name}</span>
                <Badge className="bg-emerald-500/20 text-emerald-400 border-0 font-mono-data ml-2">{p.margin_pct}%</Badge>
              </div>
            ))}
          </div></CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><TrendingDown className="w-4 h-4 text-red-500" />{t("analytics.worst_margin")}</CardTitle></CardHeader>
          <CardContent><div className="space-y-2">
            {sortedByMargin.slice(-5).reverse().map((p) => (
              <div key={p.id} className="flex items-center justify-between p-2 rounded-md hover:bg-accent/50">
                <span className="text-sm truncate flex-1">{p.name}</span>
                <Badge className={`font-mono-data border-0 ml-2 ${p.margin_pct < 0 ? "bg-red-500/20 text-red-400" : "bg-amber-500/20 text-amber-400"}`}>{p.margin_pct}%</Badge>
              </div>
            ))}
          </div></CardContent>
        </Card>
      </div>
    </div>
  );
}
